import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Line } from '../types';

interface SerialNumberFilterProps {
  lines: Line[];
  isDarkTheme?: boolean;
  onFilterChange: (serialNumber: string) => void;
}

const SerialNumberFilter: React.FC<SerialNumberFilterProps> = ({
  lines,
  isDarkTheme,
  onFilterChange
}) => {
  const [showFilter, setShowFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [serialFilter, setSerialFilter] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const availableSerialNumbers = useMemo(() => {
    return lines.map(line => line.serialNumber);
  }, [lines]);

  const filteredSerialNumbers = useMemo(() => {
    if (!searchTerm) return availableSerialNumbers;
    return availableSerialNumbers.filter(serial => 
      serial.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [availableSerialNumbers, searchTerm]);

  useEffect(() => {
    setSerialFilter('');
    setShowFilter(false);
    setSearchTerm('');
    onFilterChange('');
  }, [lines, onFilterChange]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowFilter(false);
      }
    };

    if (showFilter) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilter]);

  useEffect(() => {
    if (searchTerm) {
      const exactMatch = availableSerialNumbers.find(serial => 
        serial.toLowerCase() === searchTerm.toLowerCase()
      );
      
      if (exactMatch) {
        setSerialFilter(exactMatch);
        setShowFilter(false);
        setSearchTerm('');
        onFilterChange(exactMatch);
      } else if (filteredSerialNumbers.length === 1) {
        setSerialFilter(filteredSerialNumbers[0]);
        setShowFilter(false);
        setSearchTerm('');
        onFilterChange(filteredSerialNumbers[0]);
      }
    }
  }, [searchTerm, filteredSerialNumbers, availableSerialNumbers, onFilterChange]);

  const handleSerialSelect = (serialNum: string) => {
    setSerialFilter(serialNum);
    setShowFilter(false);
    setSearchTerm('');
    onFilterChange(serialNum);
  };

  return (
    <>
      <span 
        onClick={() => setShowFilter(!showFilter)}
        style={{ marginLeft: '5px', cursor: 'pointer', fontSize: '12px' }}
      >
        ▼
      </span>
      {showFilter && (
        <div 
          ref={dropdownRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: '0',
            backgroundColor: isDarkTheme ? '#333' : '#fff',
            border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
            borderRadius: '4px',
            zIndex: 1000,
            minWidth: '200px',
            maxHeight: '250px',
            overflowY: 'auto'
          }}>
          <div style={{ padding: '8px', borderBottom: `1px solid ${isDarkTheme ? '#555' : '#eee'}` }}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search or scan serial number..."
              style={{
                width: '100%',
                padding: '6px',
                border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
                borderRadius: '3px',
                backgroundColor: isDarkTheme ? '#222' : '#fff',
                color: isDarkTheme ? '#fff' : '#000',
                fontSize: '12px',
                boxSizing: 'border-box'
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          {filteredSerialNumbers.map((serialNum) => (
            <div
              key={serialNum}
              onClick={() => handleSerialSelect(serialNum)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                backgroundColor: serialFilter === serialNum ? (isDarkTheme ? '#555' : '#e3f2fd') : 'transparent',
                color: isDarkTheme ? '#fff' : '#000',
                borderBottom: `1px solid ${isDarkTheme ? '#555' : '#eee'}`
              }}
              onMouseEnter={(e) => {
                if (serialFilter !== serialNum) {
                  e.currentTarget.style.backgroundColor = isDarkTheme ? '#444' : '#f5f5f5';
                }
              }}
              onMouseLeave={(e) => {
                if (serialFilter !== serialNum) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              {serialNum}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default SerialNumberFilter;